import { ApiService, TApiServiceError, TApiServiceOptions } from "../apiService";

import { handleAsync } from "@/utils";
import { apiWebsocketURL } from "@/utils/env";
import { TApiResponse } from "../../types/api";
import { withAuthHooks } from "../auth/auth.ky.hooks";
import type {
  TAnswerListResponse,
  TAnswerResponse,
  TOnboardingProgress,
  TOnboardingQuestion,
  TOnboardingSummary,
  TSubmitAnswerPayload,
  TUploadAnswerFilePayload,
} from "./onboarding.types";

const apiService = new ApiService({
  hooks: {
    ...withAuthHooks,
  },
});

class OnboardingService {
  private rootEndpoint = "onboarding" as const;
  private websocketBasePath = "onboarding/ws/onboarding" as const;
  private uploadEndpoint = "files/upload" as const;

  getWebSocketURL(companyId: string, token: string) {
    const url = apiWebsocketURL(`${this.websocketBasePath}/${companyId}`);
    url.searchParams.set("token", token);
    return url.toString();
  }

  getQuestionsByRole(role: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TOnboardingQuestion[]>>(() =>
      apiService.get(`${this.rootEndpoint}/questions/${role}`, options)
    );
  }

  async submitAnswer(payload: TSubmitAnswerPayload, options?: TApiServiceOptions) {
    if (payload.file && payload.dataType === "file_upload") {
      // For file uploads, use FormData
      const formData = new FormData();
      formData.append("file", payload.file);
      formData.append("company_id", payload.companyId);
      formData.append("question_id", payload.questionId);
      formData.append("data_type", payload.dataType);
      if (payload.answerValue !== undefined && payload.answerValue !== null) {
        formData.append("answer_value", String(payload.answerValue));
      }

      return handleAsync<TApiServiceError, TApiResponse<TAnswerResponse>>(() =>
        apiService.post(`${this.rootEndpoint}/submit-answer`, formData, options)
      );
    } else {
      // For non-file answers, use JSON
      const queryParams = new URLSearchParams({
        company_id: payload.companyId,
        question_id: payload.questionId,
        data_type: payload.dataType,
      });
      if (payload.answerValue !== undefined && payload.answerValue !== null) {
        queryParams.append("answer_value", String(payload.answerValue));
      }

      return handleAsync<TApiServiceError, TApiResponse<TAnswerResponse>>(() =>
        apiService.post(`${this.rootEndpoint}/submit-answer?${queryParams.toString()}`, undefined, options)
      );
    }
  }

  getOnboardingProgress(companyId: string, role: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TOnboardingProgress>>(() =>
      apiService.get(`${this.rootEndpoint}/progress/${companyId}?role=${role}`, options)
    );
  }

  getCompanyAnswers(companyId: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TAnswerListResponse>>(() =>
      apiService.get(`${this.rootEndpoint}/answers/${companyId}`, options)
    );
  }

  getAnswerByQuestion(companyId: string, questionId: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TAnswerResponse>>(() =>
      apiService.get(`${this.rootEndpoint}/answer/${companyId}/${questionId}`, options)
    );
  }

  uploadAnswerFile(payload: TUploadAnswerFilePayload, options?: TApiServiceOptions) {
    const formData = new FormData();
    formData.append("file", payload.file);

    const endpoint = `${this.uploadEndpoint}?file_type=onboarding_answer&company_id=${payload.companyId}&context_id=${payload.questionId}`;

    return handleAsync<TApiServiceError, TApiResponse<{ id: string }>>(() =>
      apiService.post(endpoint, formData, options)
    );
  }

  getOnboardingSummary(companyId: string, options?: TApiServiceOptions, lite = false) {
    return handleAsync<TApiServiceError, TApiResponse<TOnboardingSummary>>(() =>
      apiService.get(`onboarding/summary/${companyId}${lite ? "?lite=true" : ""}`, options)
    );
  }

  retrySummaryGeneration(companyId: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<{ status: string; message: string }>>(() =>
      apiService.post(`onboarding/summary/${companyId}/retry`, undefined, options)
    );
  }

  refreshSummaryGeneration(companyId: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<{ status: string; message: string }>>(() =>
      apiService.post(`onboarding/summary/${companyId}/refresh`, undefined, options)
    );
  }

  getFilePreview(fileId: string, options?: TApiServiceOptions) {
    return handleAsync<
      TApiServiceError,
      TApiResponse<{
        file_id: string;
        file_name: string;
        content_type: string;
        file_size: number;
        preview_url: string;
        is_previewable: boolean;
        is_image: boolean;
        is_pdf: boolean;
      }>
    >(() => apiService.get(`onboarding/files/${fileId}/preview`, options));
  }

  getFileDownload(fileId: string, options?: TApiServiceOptions) {
    return handleAsync<
      TApiServiceError,
      TApiResponse<{
        file_id: string;
        file_name: string;
        content_type: string;
        file_size: number;
        download_url: string;
      }>
    >(() => apiService.get(`onboarding/files/${fileId}/download`, options));
  }

  async downloadFile(fileId: string, fileName?: string) {
    const { data, error } = await this.getFileDownload(fileId);

    if (error || !data?.data) {
      throw new Error(error?.message ?? "Failed to get download URL");
    }

    // Create a temporary link to trigger download
    const link = document.createElement("a");
    link.href = data.data.download_url;
    link.download = fileName ?? data.data.file_name ?? "download";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}

export const onboardingService = new OnboardingService();
